import type { WeaponClass } from './character-roster';

/**
 * Client-side mirror of the timing values design/gdd/character-visuals.md's
 * Formulas 1-3 need — sourced from melee-combat.md's weapon stat table and
 * movement-netcode.md's parry timing, neither of which the frontend
 * otherwise needs (attack/parry are server-resolved; the client only reacts
 * to state changes). Kept here rather than in config/tuning.ts since that
 * file mirrors Movement's own formulas specifically.
 */
/**
 * combat-depth.md amendment (2026-08-21, second) — this mirror had gone
 * stale: the backend's `WEAPONS` table (dungeon-prototype/config/tuning.ts)
 * was cut 25% on windup/recovery for pacing ("combat feels very slow") but
 * this client-only mirror was never updated to match, so every attack
 * animation kept stretching to the OLD, slower duration via
 * `playOnceTimed()` below — the swing looked and felt slower than the
 * server's actual state machine, independent of the real timing fix
 * already having landed. A likely real contributor to "combat still feels
 * stiff and wooden."
 */
export const WEAPON_TIMING: Record<WeaponClass, { windupMs: number; activeMs: number; recoveryMs: number }> = {
  LIGHT: { windupMs: 135, activeMs: 90, recoveryMs: 195 },
  MEDIUM: { windupMs: 240, activeMs: 120, recoveryMs: 300 },
  BLUNT: { windupMs: 300, activeMs: 130, recoveryMs: 360 },
  HEAVY: { windupMs: 410, activeMs: 160, recoveryMs: 490 },
  REACH: { windupMs: 330, activeMs: 110, recoveryMs: 430 },
};

export const PARRY_WINDOW_MS = 150;
export const PARRY_RECOVERY_MS = 300;

/** A passive dodge's sidestep (2026-10-16) — the pack's `Dodge_Left`/`Dodge_Right` are authored at 0.4s; a touch quicker reads as a reflex. */
export const SIDESTEP_DURATION_MS = 350;

/** Formula 3 + Edge Case #1 — clamps how far a repurposed clip can be sped up/slowed down. */
export const PLAYBACK_RATE_MIN = 0.3;
export const PLAYBACK_RATE_MAX = 3.0;

/** Edge Case #3 — cross-fade duration on every animation-state change. */
export const CROSSFADE_SECONDS = 0.1;

/**
 * Combat-feel amendment (2026-08-21) — user report: "combat still feels
 * stiff and wooden." A landed hit previously had zero visible feedback on
 * the defender's model, and nothing at all visible to a bystander or the
 * attacker. Deliberately much quicker/smaller than the ability VFX ring so
 * the two read as distinct events, not the same effect reused.
 */
export const IMPACT_VFX_DURATION_MS = 180;

/**
 * The level-up pillar (2026-08-25) — "a sound and animation like in WoW".
 *
 * Long, by this file's standards: 1.8s against the ability ring's 0.5s and the
 * hit flash's 0.18s. That asymmetry is the point. The other two are *feedback*
 * — they confirm an input and must be gone before the next one — while this is
 * a *reward*, and it has to outlast the moment that earned it or the player
 * blinks and misses their own level-up. It also has to be legible to a
 * squadmate who was looking somewhere else when it started.
 *
 * The column is deliberately taller than the character (1.8m rig, 4.2m column)
 * so it reads from across a room and through a doorway, and thin enough not to
 * hide the body it is celebrating.
 */
export const LEVEL_UP_VFX = {
  DURATION_MS: 1_800,
  COLUMN_RADIUS_M: 0.62,
  COLUMN_HEIGHT_M: 4.2,
  /** Gold, matching the ability ring's `0xfff2a8` family rather than inventing a new accent. */
  COLOR: 0xffe8a3,
  /**
   * Cut from 0.55 to 0.3 after looking at the first live capture: additive gold
   * at 0.55 washed the character to near-white and blew out a third of the
   * frame. The point is to light the figure up, not to erase it.
   */
  COLUMN_PEAK_OPACITY: 0.3,
  /** The column grows this much taller over its life, so it reads as rising rather than as a static cylinder fading. */
  COLUMN_RISE_SCALE: 1.35,
  /** A ground ring that expands outward, same idiom as the ability VFX so the two feel like one visual language. */
  RING_INNER_M: 0.5,
  RING_OUTER_M: 0.78,
  RING_EXPAND_SCALE: 3.4,
  RING_PEAK_OPACITY: 0.8,
} as const;

/**
 * Game Feel Polish Phase 2b — per-phase playback-rate multipliers applied on
 * top of `playOnceTimed()`'s base rate (which alone would play the whole
 * swing at one constant speed). Anticipation reads as a wind-up, the strike
 * itself snaps through fast, recovery settles at a medium pace — instead of
 * one flat blur from start to finish. Chosen so the weighted average across
 * a swing's actual windup/active/recovery split stays close to 1x (see
 * `computeAttackTimeScale` in character-visual.ts), keeping the total swing
 * duration close to Formula 1's contract even though phases individually
 * speed up or slow down.
 */
export const ATTACK_ANTICIPATION_MULT = 0.7;
export const ATTACK_STRIKE_MULT = 1.6;
export const ATTACK_RECOVERY_MULT = 0.9;

/**
 * Game Feel Polish Phase 2c — additive flinch layer for a landed hit that
 * doesn't trigger a full STAGGERED replace-animation (most hits — Poise &
 * Stagger's strict `>` comparison means same-tier or Light-vs-anything-but-
 * Cloth matchups show zero reaction otherwise, per character-visual.ts's own
 * `playImpactVfx` comment). Weight < 1 so it reads as a flinch layered on
 * top of whatever the character is already doing, not a full pose swap;
 * timeScale > 1 so it snaps rather than lingers.
 */
export const HIT_FLINCH_WEIGHT = 0.55;
export const HIT_FLINCH_TIME_SCALE = 1.6;

/**
 * Per-frame planar distance above which a body counts as moving, used only to
 * choose between a crouch idle and a sneak (`character-visual.ts`).
 *
 * A distance, not a speed: it is compared against an exponentially smoothed
 * per-frame step, so the units are metres-per-frame and the value is small on
 * purpose. Big enough to ignore interpolation jitter on a standing character,
 * small enough that the slowest crouch-creep still reads as movement.
 */
export const MOVING_SPEED_EPSILON = 0.004;

/**
 * How far a body must travel in one frame before the direction of that travel
 * is trusted to decide forwards-vs-backwards gait.
 *
 * Same units and spirit as MOVING_SPEED_EPSILON above (metres per frame), but a
 * separate knob because it answers a different question: not "is this body
 * moving" but "is this step long enough for its *direction* to mean anything."
 * Position arrives interpolated, so a near-stationary body's step direction is
 * mostly noise, and flipping gait on noise is worse than being a frame late.
 */
export const BACKWARD_STEP_EPSILON = 0.002;

/**
 * How far off forwards travel has to be to count as backing up: the negative
 * cosine of the angle between travel and facing.
 *
 * 0.5 = beyond 120 degrees off the way you are pointing. A pure strafe (90
 * degrees, cosine 0) keeps the forward stride, because the pack ships no strafe
 * clips and a backwards walk sold sideways looks worse than a forward one does;
 * a diagonal retreat (135 degrees, cosine -0.71) reads as backing away, which
 * is how a player actually leaves a fight.
 */
export const BACKWARD_DOT_THRESHOLD = 0.5;
